import { useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { Badge } from '../components/primitives/Badge'
import { Button } from '../components/primitives/Button'
import { Modal } from '../components/primitives/Modal'
import { PageHead } from '../components/primitives/PageHead'
import { YearNavigator } from '../components/primitives/YearNavigator'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { HrCancelLeaveDialog } from '../components/hr/HrCancelLeaveDialog'
import { HrLeaveRowActions } from '../components/hr/HrLeaveRowActions'
import { HrLeaveFilterCards } from '../components/hr/HrLeaveFilterCards'
import { LeaveDatesFilter } from '../components/hr/LeaveDatesFilter'
import { getEmployee } from '../api/employees'
import { approveLeave, cancelHrLeave, rejectLeave } from '../api/leaves'
import { ApiError } from '../api/client'
import { Icon } from '../components/primitives/Icon'
import { Tooltip } from '../components/primitives/Tooltip'
import { useToast, type ToastVariant } from '../components/primitives/Toast'
import {
  asHrLeavesFlag,
  getHrLeaveCounts,
  listHrLeavesPage,
  nudgeApprover,
  type HrLeavesFlag,
  type HrLeavesFilters,
  type HrLeavesSortKey,
  type HrLeaveRow,
} from '../api/hr-leaves'
import { usePagedList } from '../lib/use-paged-list'
import { PaginationControls } from '../components/lists/PaginationControls'
import { useDebounce } from '../lib/use-debounce'
import { useUrlSearch } from '../lib/use-url-search'
import { flags } from '../lib/feature-flags'
import {
  leaveYearBounds,
  resolveLeaveYear,
  searchWithDateRange,
  searchWithYear,
} from '../lib/leave-year'
import type { LeaveTypeId } from '@lms/shared-types'

/**
 * HR-only filtered-leaves surface backing the home AI cards' "drill into
 * a specific cluster" deep-links. All filters live on the URL so anyone
 * can construct a link that opens the right slice — `?type=medical&
 * manager=<uuid>&since=<iso-date>` etc.
 *
 * The page is reactive to URL changes. No internal filter state — the
 * user navigates via deep-links, not via on-page controls (those can
 * come in a follow-up when HR needs to filter ad-hoc).
 */
export function HrLeavesPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <HrLeavesContent />
    </HrAccessGuard>
  )
}

function HrLeavesContent(): JSX.Element {
  const search = useSearch({ strict: false }) as HrLeavesFilters
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Free-text search and column sort are both sent to the server so they apply
  // across ALL leaves, not just the loaded page.
  // The term lives on the URL (`?q=`, shared with the Entitlement/Balances
  // tabs) so it survives tab switches and modal open/close.
  const [query, setQuery] = useUrlSearch({ to: '/hr/leaves' })
  const [sort, setSort] = useState<SortState | null>(null)

  // Per-row actions (View / Approve / Reject / Cancel) live in a ⋮ menu. HR may
  // decide any leave (`can_decide`: HR+). Approve is zero-friction; Reject and
  // Cancel take a reason dialog. Every mutation refreshes the hr-leaves windows.
  const [cancelRow, setCancelRow] = useState<HrLeaveRow | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [rejectRow, setRejectRow] = useState<HrLeaveRow | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectError, setRejectError] = useState<string | null>(null)
  const [rejectPending, setRejectPending] = useState(false)
  // Floating toaster (fixed top-right, above everything) — visible no matter
  // how far down the table the HR admin has scrolled.
  const { notify } = useToast()
  const flashToast = (msg: string, variant: ToastVariant = 'success'): void => {
    notify(msg, variant)
  }

  const openLeave = (row: HrLeaveRow): void => {
    void navigate({ to: '/leave', search: { id: row.id } })
  }

  async function handleApprove(row: HrLeaveRow): Promise<void> {
    try {
      await approveLeave(row.id)
      await queryClient.invalidateQueries({ queryKey: ['hr-leaves'] })
      flashToast(`${row.employeeName}'s leave has been approved.`)
    } catch (err: unknown) {
      flashToast(err instanceof Error ? err.message : 'Approve failed.', 'error')
    }
  }

  async function handleNudge(row: HrLeaveRow): Promise<void> {
    try {
      await nudgeApprover(row.id)
      flashToast(`Nudged the approver for ${row.employeeName}'s leave.`)
    } catch (err: unknown) {
      // 429 cooldown (and 422) come back as a bare { code, message } body,
      // surfaced on ApiError.details — prefer that friendly message.
      const detail =
        err instanceof ApiError && err.details && typeof err.details === 'object'
          ? (err.details as { message?: string }).message
          : undefined
      // A cooldown (429) is expected, not a hard failure — show it as a warning.
      const variant: ToastVariant =
        err instanceof ApiError && err.status === 429 ? 'warning' : 'error'
      flashToast(detail ?? (err instanceof Error ? err.message : 'Nudge failed.'), variant)
    } finally {
      // Refetch so the bell reflects the new cooldown (server wrote the audit
      // even when Slack delivery no-ops, and a 429 means state drifted).
      await queryClient.invalidateQueries({ queryKey: ['hr-leaves'] })
    }
  }

  async function handleConfirmCancel(reason: string): Promise<void> {
    if (!cancelRow) return
    try {
      await cancelHrLeave(cancelRow.id, reason)
      flashToast(`Leave for ${cancelRow.employeeName} has been cancelled.`)
      setCancelRow(null)
      await queryClient.invalidateQueries({ queryKey: ['hr-leaves'] })
    } catch (err: unknown) {
      setCancelError(err instanceof Error ? err.message : 'An error occurred.')
    }
  }

  async function handleConfirmReject(): Promise<void> {
    if (!rejectRow) return
    const reason = rejectReason.trim()
    if (reason.length === 0) {
      setRejectError('Please give a rejection reason.')
      return
    }
    setRejectError(null)
    setRejectPending(true)
    try {
      await rejectLeave(rejectRow.id, reason)
      flashToast(`${rejectRow.employeeName}'s leave has been rejected.`)
      setRejectRow(null)
      setRejectReason('')
      await queryClient.invalidateQueries({ queryKey: ['hr-leaves'] })
    } catch (err: unknown) {
      setRejectError(err instanceof Error ? err.message : 'Reject failed.')
    } finally {
      setRejectPending(false)
    }
  }

  // Free-text search runs SERVER-side (across all leaves, not just the loaded
  // window). Debounced so we don't refetch on every keystroke.
  const debouncedQuery = useDebounce(query.trim(), 300)

  // Numbered/offset pagination — hits the route with `page`, which flips it to
  // the enveloped shape carrying an exact `total`. Filters (`search` object) +
  // the debounced free-text query are in the hook's queryKey, so a filter or
  // search change jumps back to page 1.
  // Sort is in the queryKey too, so a header click jumps back to page 1 of the
  // freshly ordered set.
  // Year filter: `?year=` on the URL (absent = current year). Leaves are scoped
  // by START date on the server. Flag OFF → the key is ignored entirely.
  const yearBounds = leaveYearBounds()
  const year = resolveLeaveYear(search.year)
  const { year: _urlYear, ...searchWithoutYear } = search
  const scopedSearch: HrLeavesFilters = flags.leaveYearFilter
    ? { ...searchWithoutYear, year: String(year) }
    : searchWithoutYear
  const setYear = (next: number): void => {
    void navigate({ to: '/hr/leaves', search: searchWithYear(search as Record<string, string>, next, yearBounds) })
  }

  const pagedList = usePagedList<HrLeaveRow>({
    queryKey: ['hr-leaves', scopedSearch, debouncedQuery, sort],
    fetcher: ({ page, pageSize }) =>
      listHrLeavesPage({
        ...scopedSearch,
        ...(debouncedQuery ? { search: debouncedQuery } : {}),
        ...(sort ? { sort: sort.key, dir: sort.dir } : {}),
        page,
        limit: pageSize,
      }).then((r) => ({ rows: r.data, total: r.meta.total })),
  })

  const rows = pagedList.rows
  const isLoading = pagedList.isLoading
  const isError = pagedList.isError

  // Filter cards: counts follow every other filter but never the selected card,
  // so each card keeps its own number. Shares the 'hr-leaves' key prefix, so
  // the mutations' invalidation refreshes the counts too.
  const activeFlag = asHrLeavesFlag(search.flag)
  const { flag: _selectedFlag, ...countFilters } = scopedSearch
  const countsQuery = useQuery({
    queryKey: ['hr-leaves', 'counts', countFilters, debouncedQuery],
    queryFn: () =>
      getHrLeaveCounts({ ...countFilters, ...(debouncedQuery ? { search: debouncedQuery } : {}) }),
    enabled: flags.hrLeavesFilterCards,
  })
  const toggleFlag = (flag: HrLeavesFlag): void => {
    setFilter('flag', activeFlag === flag ? '' : flag)
  }

  const managerQuery = useQuery({
    queryKey: ['employee', search.manager],
    queryFn: () => (search.manager ? getEmployee(search.manager) : Promise.resolve(null)),
    enabled: typeof search.manager === 'string' && search.manager.length > 0,
  })

  const subtitle = describeFilters(search, managerQuery.data?.name ?? undefined)

  // Patch one filter on the URL; empty value drops the key. Manager/team
  // deep-link params are preserved (spread of existing search).
  // Leave Dates range (since/until) in one navigation; tied to the year control.
  const setLeaveDates = (since: string | null, until: string | null): void => {
    const next = searchWithDateRange(search as Record<string, string>, since, until, year, yearBounds)
    void navigate({ to: '/hr/leaves', search: next })
  }

  const setFilter = (key: keyof HrLeavesFilters, value: string): void => {
    const next: Record<string, string> = { ...(search as Record<string, string>) }
    if (value === '') delete next[key as string]
    else next[key as string] = value
    void navigate({ to: '/hr/leaves', search: next })
  }

  // Clear every filter in ONE navigation. Firing setFilter per key raced —
  // each call navigated off the same stale `search`, so only the last write
  // (until) survived and the rest lingered. Drop all filter keys + the `q`
  // search param at once; useUrlSearch re-syncs the search box from the URL.
  const clearFilters = (): void => {
    const next: Record<string, string> = { ...(search as Record<string, string>) }
    for (const key of ['type', 'status', 'since', 'until', 'q', 'flag']) delete next[key]
    void navigate({ to: '/hr/leaves', search: next })
  }

  const toggleSort = (key: SortKey): void => {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    )
  }

  return (
    <>
      <PageHead
        eyebrow="HR"
        title="Leave Applications"
        {...(subtitle ? { subtitle } : {})}
        {...(flags.leaveYearFilter
          ? {
              actions: (
                <YearNavigator
                  year={year}
                  min={yearBounds.min}
                  max={yearBounds.max}
                  onChange={setYear}
                />
              ),
            }
          : {})}
      />

      {flags.hrLeavesFilterCards ? (
        <HrLeaveFilterCards counts={countsQuery.data} active={activeFlag} onToggle={toggleFlag} />
      ) : null}

      <FilterBar
        search={search}
        onChange={setFilter}
        onClear={clearFilters}
        query={query}
        onQuery={setQuery}
        {...(flags.leaveYearFilter
          ? {
              leaveDates: {
                onChange: setLeaveDates,
                min: `${yearBounds.min}-01-01`,
                max: `${yearBounds.max}-12-31`,
                // Open on the chosen year; the current year opens on today.
                initialView: year === yearBounds.max ? undefined : `${year}-01-01`,
              },
            }
          : {})}
      />

      {isLoading ? (
        <div style={{ padding: 24 }}>
          <ThinkingDots />
        </div>
      ) : isError ? (
        <div className="placeholder" role="alert" style={{ color: 'var(--danger-fg)' }}>
          Couldn’t load leaves. Check your connection and try again.
        </div>
      ) : rows.length === 0 ? (
        <div className="placeholder">No leaves match these filters.</div>
      ) : (
        <>
          <LeavesTable
            rows={rows}
            sort={sort}
            onSort={toggleSort}
            onView={openLeave}
            onApprove={(row) => void handleApprove(row)}
            onReject={(row) => {
              setRejectError(null)
              setRejectReason('')
              setRejectRow(row)
            }}
            onCancel={(row) => {
              setCancelError(null)
              setCancelRow(row)
            }}
            onNudge={(row) => void handleNudge(row)}
            footer={
              <PaginationControls
                page={pagedList.page}
                pageSize={pagedList.pageSize}
                total={pagedList.total}
                totalPages={pagedList.totalPages}
                pageSizeOptions={pagedList.pageSizeOptions}
                rangeStart={pagedList.rangeStart}
                rangeEnd={pagedList.rangeEnd}
                canPrev={pagedList.canPrev}
                canNext={pagedList.canNext}
                onPageChange={pagedList.setPage}
                onPageSizeChange={pagedList.setPageSize}
                isFetching={pagedList.isFetching}
              />
            }
          />
        </>
      )}

      {cancelRow ? (
        <HrCancelLeaveDialog
          employeeName={cancelRow.employeeName}
          leaveType={cancelRow.type as LeaveTypeId}
          startDate={cancelRow.startDate}
          endDate={cancelRow.endDate}
          error={cancelError}
          onClose={() => {
            setCancelRow(null)
            setCancelError(null)
          }}
          onConfirm={handleConfirmCancel}
        />
      ) : null}

      <Modal
        open={Boolean(rejectRow)}
        onClose={() => {
          if (!rejectPending) {
            setRejectRow(null)
            setRejectError(null)
          }
        }}
        title={rejectRow ? `Reject — ${rejectRow.employeeName}` : 'Reject leave'}
        footer={
          <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setRejectRow(null)
                setRejectError(null)
              }}
              disabled={rejectPending}
            >
              Keep pending
            </Button>
            <Button
              variant="destructive"
              size="md"
              data-testid="reject-confirm"
              onClick={() => void handleConfirmReject()}
              loading={rejectPending}
            >
              Confirm reject
            </Button>
          </span>
        }
      >
        <label
          htmlFor="hr-reject-reason"
          style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}
        >
          Rejection reason <span style={{ color: 'var(--danger-fg)' }}>*</span>
        </label>
        <textarea
          id="hr-reject-reason"
          data-testid="reject-reason"
          value={rejectReason}
          onChange={(e) => {
            setRejectReason(e.target.value)
            setRejectError(null)
          }}
          placeholder="Tell the employee why this leave was rejected…"
          rows={4}
          disabled={rejectPending}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 8,
            border: `1px solid ${rejectError ? 'var(--danger-line)' : 'var(--border)'}`,
            background: 'var(--surface)',
            fontSize: 14,
            color: 'var(--text)',
            resize: 'vertical',
            boxSizing: 'border-box',
            fontFamily: 'var(--font-sans)',
          }}
        />
        {rejectError ? (
          <div
            role="alert"
            data-testid="reject-error"
            style={{
              marginTop: 8,
              padding: '8px 12px',
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {rejectError}
          </div>
        ) : null}
      </Modal>
    </>
  )
}

// ── server-side sort ─────────────────────────────────────────────────────────

// Sort state stays local (keeps header clicks off the URL); the key is the
// API's `sort` value, so it is passed through to the request as-is.
type SortKey = HrLeavesSortKey
interface SortState {
  key: SortKey
  dir: 'asc' | 'desc'
}

const TYPE_OPTIONS = [
  'annual', 'medical', 'hospitalization', 'unpaid', 'birthday',
  'compassionate', 'maternity', 'paternity', 'marriage',
  // Retired: not applyable, but kept in the filter so HR can still find its
  // history. (Applies only to the line below, NOT to replacement.)
  'marriage_family_care',
  'replacement',
] as const
const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'escalated', 'cancelled'] as const

/** On-page filter controls. State lives on the URL so links stay shareable. */
function FilterBar({
  search,
  onChange,
  onClear,
  query,
  onQuery,
  leaveDates,
}: Readonly<{
  search: HrLeavesFilters
  onChange: (key: keyof HrLeavesFilters, value: string) => void
  onClear: () => void
  query: string
  onQuery: (value: string) => void
  /** Year-filter mode: one "Leave Dates" range picker replaces From / To. */
  leaveDates?: {
    onChange: (since: string | null, until: string | null) => void
    min: string
    max: string
    initialView: string | undefined
  }
}>): JSX.Element {
  const hasFilters = Boolean(
    search.type || search.status || search.since || search.until || search.flag || query,
  )
  return (
    <div style={filterBarStyle}>
      <label style={{ ...filterLabelStyle, flex: '1 1 220px' }}>
        Search
        <input
          type="search"
          placeholder="Employee name or reason…"
          style={filterControlStyle}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </label>
      <label style={filterLabelStyle}>
        Type
        <select style={filterControlStyle} value={search.type ?? ''} onChange={(e) => onChange('type', e.target.value)}>
          <option value="">All</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{typeLabel(t)}</option>
          ))}
        </select>
      </label>
      <label style={filterLabelStyle}>
        Status
        <select style={filterControlStyle} value={search.status ?? ''} onChange={(e) => onChange('status', e.target.value)}>
          <option value="">All</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </label>
      {leaveDates ? (
        <div style={filterLabelStyle}>
          <span id="hr-leaves-leave-dates-label">Leave Dates</span>
          <LeaveDatesFilter
            since={search.since}
            until={search.until}
            onChange={leaveDates.onChange}
            min={leaveDates.min}
            max={leaveDates.max}
            initialView={leaveDates.initialView}
            labelId="hr-leaves-leave-dates-label"
            controlStyle={filterControlStyle}
          />
        </div>
      ) : (
        <>
          <label style={filterLabelStyle}>
            From
            <input type="date" style={filterControlStyle} value={search.since ?? ''} onChange={(e) => onChange('since', e.target.value)} />
          </label>
          <label style={filterLabelStyle}>
            To
            <input type="date" style={filterControlStyle} value={search.until ?? ''} onChange={(e) => onChange('until', e.target.value)} />
          </label>
        </>
      )}
      {hasFilters ? (
        <button type="button" style={clearButtonStyle} onClick={onClear}>
          Clear
        </button>
      ) : null}
    </div>
  )
}

const NUDGE_COOLDOWN_MS = 24 * 60 * 60 * 1000

/** Whether a pending leave is still inside its 24h nudge cooldown, and how
 *  many whole hours remain — mirrors the server's per-leave guard so the bell
 *  can show a muted/"paused" state instead of clicking through to a 429. */
function nudgeCooldown(lastNudgedAt: string | null): {
  active: boolean
  hoursLeft: number
} {
  if (!lastNudgedAt) return { active: false, hoursLeft: 0 }
  const remaining = new Date(lastNudgedAt).getTime() + NUDGE_COOLDOWN_MS - Date.now()
  if (remaining <= 0) return { active: false, hoursLeft: 0 }
  return { active: true, hoursLeft: Math.ceil(remaining / 3_600_000) }
}

interface RowActions {
  onView: (row: HrLeaveRow) => void
  onApprove: (row: HrLeaveRow) => void
  onReject: (row: HrLeaveRow) => void
  onCancel: (row: HrLeaveRow) => void
  onNudge: (row: HrLeaveRow) => void
}

function LeavesTable({
  rows,
  sort,
  onSort,
  footer,
  ...actions
}: Readonly<
  {
    rows: ReadonlyArray<HrLeaveRow>
    sort: SortState | null
    onSort: (key: SortKey) => void
    /** Rendered inside the card beneath the table (e.g. PaginationControls). */
    footer?: ReactNode
  } & RowActions
>): JSX.Element {
  const showApplied = flags.hrLeavesAppliedColumn
  return (
    <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      {/* Fixed layout with proportional widths (sum 100%) so the table fits one
          view without horizontal scroll; the Employee cell truncates. */}
      <table className="tbl" style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            <SortableTh label="Employee" sortKey="employee_name" sort={sort} onSort={onSort} width={showApplied ? '20%' : '22%'} />
            <SortableTh label="Type" sortKey="type" sort={sort} onSort={onSort} width={showApplied ? '14%' : '16%'} />
            <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} width="12%" />
            <SortableTh label="Dates" sortKey="start_date" sort={sort} onSort={onSort} width={showApplied ? '15%' : '18%'} />
            <SortableTh label="Days" sortKey="days" sort={sort} onSort={onSort} align="right" width={showApplied ? '7%' : '8%'} />
            {showApplied ? (
              <SortableTh label="Applied" sortKey="applied_at" sort={sort} onSort={onSort} width="12%" />
            ) : null}
            <SortableTh label="Updated" sortKey="updated_at" sort={sort} onSort={onSort} width={showApplied ? '12%' : '16%'} />
            <th aria-label="Actions" style={{ width: '8%' }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <LeaveRow key={row.id} row={row} actions={actions} />
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  )
}

/** Clickable column header. Shows ▲/▼ for the active sort key; click toggles
 *  direction. aria-sort keeps it accessible to screen readers. */
function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  align,
  width,
}: Readonly<{
  label: string
  sortKey: SortKey
  sort: SortState | null
  onSort: (key: SortKey) => void
  align?: 'right'
  /** Proportional column width for the fixed table layout. */
  width?: string
}>): JSX.Element {
  const active = sort?.key === sortKey
  const arrow = active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''
  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
  return (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', ...(width ? { width } : {}), ...(align === 'right' ? { textAlign: 'right' } : {}) }}
      aria-sort={ariaSort}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSort(sortKey)
        }
      }}
      tabIndex={0}
      role="columnheader button"
    >
      {label}
      <span aria-hidden="true" style={{ color: active ? 'var(--text)' : 'var(--text-3)' }}>{arrow || ' ↕'}</span>
    </th>
  )
}

/** One clickable row — clicking the row (outside the ⋮ menu) opens the leave
 *  detail. Per-row View / Approve / Reject / Cancel live in the overflow menu.
 *  Enter / Space activate the row via keyboard. */
function LeaveRow({
  row,
  actions,
}: Readonly<{ row: HrLeaveRow; actions: RowActions }>): JSX.Element {
  const navigate = useNavigate()

  const go = (): void => {
    void navigate({ to: '/leave', search: { id: row.id } })
  }
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      go()
    }
  }

  return (
    <tr
      style={{ cursor: 'pointer' }}
      tabIndex={0}
      role="button"
      aria-label={`Open ${row.employeeName}'s ${typeLabel(row.type)} on ${formatDateRange(row.startDate, row.endDate)}`}
      onClick={go}
      onKeyDown={onKey}
    >
      <td style={{ fontWeight: 500 }}>
        <span
          title={row.employeeName}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {row.employeeName}
        </span>
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {typeLabel(row.type)}
          {row.isEmergency ? (
            <Badge
              variant="danger"
              tooltip="Annual leave filed with less than 7 days notice. Classified as Emergency Leave (Policy §1.1)."
            >
              Emergency
            </Badge>
          ) : null}
          {row.isOutOfSystem ? (
            <Badge
              variant="info"
              tooltip="Encoded by HR out of system (approved offline, e.g. unpaid or blackout-period leave)."
            >
              Out of system
            </Badge>
          ) : null}
          {row.approvedOverCapacity ? (
            <Badge
              variant="warning"
              tooltip="Approved while the team was over its concurrent-absence cap. Recorded for HR oversight."
            >
              Above capacity
            </Badge>
          ) : null}
        </span>
      </td>
      <td>
        <span className={statusBadgeClass(row.status)}>{row.status.replace(/_/g, ' ')}</span>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatDateRange(row.startDate, row.endDate)}</td>
      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.days}</td>
      {flags.hrLeavesAppliedColumn ? (
        <td style={{ color: 'var(--text-2)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
          {row.appliedAt ? (
            formatDate(row.appliedAt)
          ) : (
            <Tooltip content="Imported from the previous system, which didn't record when leave was applied.">
              <span aria-label="Applied date not recorded (imported from the previous system)" tabIndex={0}>
                —
              </span>
            </Tooltip>
          )}
        </td>
      ) : null}
      <td style={{ color: 'var(--text-2)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {formatDate(row.updatedAt)}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {row.status === 'pending'
            ? (() => {
                const cd = nudgeCooldown(row.lastNudgedAt)
                return (
                  <Tooltip
                    content={
                      cd.active
                        ? `Nudged in the last 24h — try again in ~${cd.hoursLeft}h`
                        : 'Nudge approver'
                    }
                    placement="top"
                    delay={0}
                  >
                    <button
                      type="button"
                      aria-label={
                        cd.active
                          ? `Approver already nudged — available again in about ${cd.hoursLeft} hour${cd.hoursLeft === 1 ? '' : 's'}`
                          : `Nudge the approver for ${row.employeeName}'s leave`
                      }
                      aria-disabled={cd.active}
                      data-testid="hr-leave-nudge"
                      data-cooldown={cd.active ? 'true' : 'false'}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!cd.active) actions.onNudge(row)
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        padding: 0,
                        background: 'transparent',
                        border: '1px solid transparent',
                        borderRadius: 8,
                        cursor: cd.active ? 'default' : 'pointer',
                        opacity: cd.active ? 0.4 : 1,
                        color: cd.active ? 'var(--text-2)' : 'var(--primary)',
                      }}
                    >
                      <Icon
                        name={cd.active ? 'notifications_paused' : 'notifications_active'}
                        size={18}
                        aria-hidden
                      />
                    </button>
                  </Tooltip>
                )
              })()
            : null}
          <HrLeaveRowActions
            status={row.status}
            canCancel={row.status === 'approved' && flags.hrCancelApproved}
            onView={() => actions.onView(row)}
            onApprove={() => actions.onApprove(row)}
            onReject={() => actions.onReject(row)}
            onCancel={() => actions.onCancel(row)}
          />
        </span>
      </td>
    </tr>
  )
}

function describeFilters(f: HrLeavesFilters, managerName?: string): string {
  // Only surface context the on-page controls DON'T already show — the
  // manager/team scope (reachable via deep-link, with no filter control). Type,
  // status, dates and search are omitted so the subtitle doesn't just echo the
  // filter row sitting right below it.
  if (managerName) return `${managerName}'s team`
  if (f.team_id) return 'one team'
  return ''
}

function typeLabel(t: string): string {
  switch (t) {
    case 'medical':
      return 'Sick leave'
    case 'annual':
      return 'Annual leave'
    case 'unpaid':
      return 'Unpaid leave'
    case 'hospitalization':
      return 'Hospitalization'
    case 'birthday':
      return 'Birthday'
    case 'compassionate':
      return 'Compassionate'
    case 'maternity':
      return 'Maternity'
    case 'paternity':
      return 'Paternity'
    case 'marriage':
      return 'Marriage'
    case 'marriage_family_care':
      return 'Marriage & Family Care'
    case 'replacement':
      return 'Replacement'
    default:
      return t.charAt(0).toUpperCase() + t.slice(1)
  }
}

function formatDateRange(start: string, end: string): string {
  if (start === end) {
    return formatDate(start)
  }
  // Full "dd MMM yyyy" on both endpoints — "03 Jun 2026 – 05 Jun 2026".
  return `${formatDate(start)} – ${formatDate(end)}`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Status → shared `.badge.dot.b-*` pill class (same vocabulary as My Leaves). */
function statusBadgeClass(status: string): string {
  switch (status) {
    case 'approved':
      return 'badge dot b-success'
    case 'rejected':
      return 'badge dot b-danger'
    case 'pending':
    case 'escalated':
    case 'pending_cancellation':
      return 'badge dot b-warning'
    default:
      return 'badge dot b-gray'
  }
}

const filterBarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: 12,
  marginBottom: 16,
}

const filterLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-3)',
}

const filterControlStyle: CSSProperties = {
  minHeight: 36,
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 'normal',
}

const clearButtonStyle: CSSProperties = {
  minHeight: 36,
  padding: '6px 14px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-2)',
  fontSize: 13,
  cursor: 'pointer',
}
